import React, { useContext, useEffect, useState, useCallback } from 'react';
import { FormikHelpers, FormikErrors } from 'formik';
import { AxiosResponse } from 'axios';
import { useLocation } from 'react-router-dom';

import Alert from '../../atoms/Alert/Alert';
import { AirDropContext } from '../../../utils/AirDropProvider';
import { InputFormGroup } from '../../atoms/InputFormGroup/InputFormGroup';
import Link from '../../atoms/Link/Link';
import PasswordInputGroup from '../../atoms/PasswordInputGroup/PasswordFormGroup';
import SelectionFormGroup from '../../atoms/SelectionFormGroup/SelectionFormGroup';
import { getSocialSignIn } from '../../../utils/validationUtils';
import useForm from '../../hooks/useForm';
import Button from '../../atoms/Button/Button';
// import * as Link from '../../atoms/Link/Link';
import { ROUTES } from '../../../constants/routes.js';

import { event, postWebcredentials, WebcredentialsResponseData, WebcredentialsData, forgerockSignIn } from '../../../services';
// I don't really like this- trying to find a way to reconfigure the import/extensions rule
import * as Constants from '../../constants';
import GoogleSignIn from '../GoogleSignIn/GoogleSignIn';
import MicrosoftSignIn from '../MicrosoftSignIn/MicrosoftSignIn';
import ZainodigitaleSignIn from '../ZainodigitaleSignIn/ZainodigitaleSignIn';
import {
  getErrorMessageFromPiErrorCode,
  getErrorMessageFromSocialSigninV2Error,
  getPiErrorCodeFromAxiosError,
} from '../../../utils/errorUtils';
import useTransitionalNavigate from '../../hooks/useTransitionalNavigate';
import useFeatures from '../../hooks/useFeatures';
import { SIGNIN_CATEGORY, SIGNIN_NAME, USER_INTERACTION_NAME } from '../../constants/googleAnalytics4Event';
import { RecaptchaContext } from '../../context/Recaptcha';
import SchoolDomainInputGroup from '../../atoms/SchoolDomainInputGroup/SchoolDomainInputGroup';
import SvgIcon from '../SvgIcon/SvgIcon';
import useShowError from '../../hooks/useShowError';
import { AUTOLOGIN_ACTIONS, SIGNIN_ACTIONS } from '../../constants/recaptcha';
import useTranslation from '../../hooks/useTranslation';
import useLocale from '../../hooks/useLocale';

interface FormValues {
  username: string;
  password: string;
  schoolCode?: string;
  rememberMe: boolean;
}

interface SignInFormProps {
  initialError?: InitialErrorType | undefined;
}

const gaEventName = SIGNIN_NAME;
const gaEventCategory = SIGNIN_CATEGORY;

function SignInForm({ initialError = Constants.defaultInitialError }: SignInFormProps) {
  const [globalErrorMessage, setGlobalErrorMessage] = useState<InitialErrorType | undefined>(initialError);
  const { showError } = useShowError();
  const [schoolDomain, setSchoolDomain] = useState<string>('');
  const [masterShowSchoolDomain, setMasterShowSchoolDomain] = useState<boolean>(false);

  const config = useContext(AirDropContext);
  const recaptcha = useContext(RecaptchaContext);
  const { translateText } = useTranslation();
  const navigate = useTransitionalNavigate();
  const { getLocale } = useLocale();
  const currentLanguage = getLocale();

  const { search, state } = useLocation();
  const { signInErrorId, showSchoolCode, showSchoolDomain, showKidFriendlyIcons, sesstype } = config;

  const params = new URLSearchParams(search);

  const { isLegacy } = useFeatures();
  const isAutoLogin = (params?.get('auto_login') === 'true') || false;
  const recaptchaAction = recaptcha?.getRecaptchaAction(isAutoLogin ? AUTOLOGIN_ACTIONS : SIGNIN_ACTIONS);

  const eventGoToForgotUnPw = () => {
    event(USER_INTERACTION_NAME, gaEventCategory, 'Link', `${gaEventCategory} Forgot Username or Password link`);
  };

  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    if (!values.username) {
      errors.username = Constants.ERRMSG_USERNAME_REQUIRED;
    }

    if (!values.password) {
      errors.password = Constants.ERRMSG_PASSWORD_REQUIRED;
    }

    if (!values.schoolCode && showSchoolCode) {
      errors.schoolCode = Constants.ERRMSG_SCHOOL_CODE_REQUIRED;
    }

    return errors;
  };

  const handleForgeRockSignInError = (error: any, formikBag: FormikHelpers<FormValues>) => {
    const errorStatus = error?.response?.status;

    let errorMessage = getErrorMessageFromPiErrorCode(Constants.PIUIERRCODE_GENERAL_SERVER_ERROR);
    if (errorStatus === 401) {
      errorMessage = getErrorMessageFromPiErrorCode(Constants.PIUIERRCODE_AUTH_FAILURE);
    }

    event(gaEventName, gaEventCategory, 'Error', `${gaEventCategory} ${errorMessage}`);

    setGlobalErrorMessage(errorMessage);
    formikBag.setSubmitting(false);
  };

  const handleSignInError = (error: any, formikBag: FormikHelpers<FormValues>) => {
    const errorStatus = error?.response?.status;

    if (errorStatus === 403) {
      params.append('escrowTicket', `${error.response.data.message}`);
      navigate({
        pathname: ROUTES.CONSENTS_INTERNAL,
        search: params.toString(),
      });
    } else {
      const PiErrorCode = getPiErrorCodeFromAxiosError(error);
      const errorMessage = getErrorMessageFromPiErrorCode(PiErrorCode);
      event(gaEventName, gaEventCategory, 'Error', `${gaEventCategory} ${errorMessage}`);
      setGlobalErrorMessage(errorMessage);
    }

    formikBag.setSubmitting(false);
  };

  const getRedirectUrl = (webcredentialsStatus: string, webcredentialsData: WebcredentialsData) => {
    if (webcredentialsStatus === Constants.SMS_REDIRECT_STATUS) {
      return webcredentialsData.smsRedirectUrl;
    }

    if (webcredentialsData.loginSuccessUrl) {
      return webcredentialsData.loginSuccessUrl;
    }

    return config?.applicationRootUrl;
  };

  const handleSignInSuccess = (response: AxiosResponse<WebcredentialsResponseData>) => {
    const webcredentialsData = response?.data?.data;
    const webcredentialsStatus = response?.data?.status;
    event(gaEventName, gaEventCategory, 'Success', `${gaEventCategory} success`);

    const redirectUrl = getRedirectUrl(webcredentialsStatus, webcredentialsData);
    if (webcredentialsData?.mfaSignInEscrow) {
      navigate({ pathname: ROUTES.UNVERIFIED_RECAPTCHA, search }, { state: webcredentialsData });
      return;
    }
    if (webcredentialsData.forceResetPasswordUrl) {
      // TODO: Implement $scope.updateLastUserActivityTime();
      // TODO: Implement $scope.getTokenfromResponse(response.data, config.CLIENT_ID);
      window.location.href = webcredentialsData.forceResetPasswordUrl;
      return;
    }

    const dobPrivacyInterstitial = config?.dobFormatTypePrivacyInterstitial;
    const showDOB = dobPrivacyInterstitial && dobPrivacyInterstitial !== 'hide';
    if (isLegacy) {
      if (!webcredentialsData.homeCountryCode || (!webcredentialsData.dob && showDOB && !webcredentialsData.isSkipDobPrompt)) {
        const redirectState = {
          redirectUrl,
          webcredentialsData,
        };
        navigate({ pathname: ROUTES.ADD_HOME_COUNTRY, search }, { state: redirectState });
        return;
      }

      // TODO: Storage keys in localstorage
      const enableResetPwViaText = config?.masterResetPwViaTextEnabled && config?.enableResetPwViaText;
      if (enableResetPwViaText) {
        const { remindToAddMobileNumber } = webcredentialsData;
        if (remindToAddMobileNumber) {
          const redirectState = {
            redirectUrl,
            flow: 'signInPrompt',
          };
          navigate({ pathname: ROUTES.ADD_MOBILE_PHONE, search }, { state: redirectState });
          return;
        }
      }
    }

    window.location.href = redirectUrl;
  };

  const transformUsername = (un: string, schoolCode?: string) => {
    let username = un;

    if (showSchoolCode && schoolCode) {
      const schoolCodeSuffix = `_${schoolCode}`;

      const alreadyHasSuffixCodeAttached = username.endsWith(schoolCodeSuffix);
      if (!alreadyHasSuffixCodeAttached) {
        username += schoolCodeSuffix;
      }
    } else if (masterShowSchoolDomain && schoolDomain) {
      const schoolDomainSuffix = `@${schoolDomain}`;

      const alreadyHasSchoolDomainAttached = username.includes('@');
      if (!alreadyHasSchoolDomainAttached) {
        username += schoolDomainSuffix;
      }
    }

    return username;
  };

  const saveSchoolCodeAndDomainFromUNToLocalStorage = (username: string, schoolCode?: string) => {
    if (showSchoolCode && schoolCode) {
      window.localStorage.setItem('ALSchoolCode', schoolCode);
    }

    if (showSchoolDomain) {
      const atSignIdx = username.indexOf('@');
      if (atSignIdx >= 0) {
        const domain = username.slice(atSignIdx + 1);
        window.localStorage.setItem('AHSchoolDomain', domain);
      }
    }
  };

  const signIn = async (signinData: SignInData, formikBag: FormikHelpers<FormValues>) => {
    let token = '';
    if (recaptcha?.isEnabled() ?? false) {
      token = await recaptcha?.executeScore(recaptchaAction);
    }

    if (sesstype === Constants.FR_SESSION_TYPE) {
      try {
        await forgerockSignIn(signinData);

        window.location.href = config?.applicationRootUrl;
      } catch (error: any) {
        handleForgeRockSignInError(error, formikBag);
      }
    } else {
      try {
        const response = await postWebcredentials(signinData, token);
        handleSignInSuccess(response);
      } catch (error: any) {
        handleSignInError(error, formikBag);
      }
    }
  };

  const handleValidatedSignInValues = async (values: FormValues, formikBag: FormikHelpers<FormValues>) => {
    const transformedUsername = transformUsername(values.username.trim(), values.schoolCode);

    const signinData: SignInData = {
      grant_type: 'password',
      username: transformedUsername,
      password: values.password.trim(),
      login_success_url: config?.applicationRootUrl,
      client_id: config?.CLIENT_ID,
      lang: currentLanguage,
    };

    if (params?.has('cookieConsentGroups') ?? config?.cookieConsentGroups) {
      signinData.cookieConsentGroups = params?.get('cookieConsentGroups') ?? config?.cookieConsentGroups;
    }

    if (params?.has('siteid') ?? config?.siteid) {
      signinData.siteid = params?.get('siteid') ?? config?.siteid;
    }

    if (params?.has('iscourseaware') ?? config?.iscourseaware) {
      signinData.iscourseaware = params?.get('iscourseaware') ?? config?.iscourseaware;
    }

    if (params?.has('okurl') ?? config?.okurl) {
      signinData.okurl = params?.get('okurl') ?? config?.okurl;
    }

    if (params?.has('errurl') ?? config?.errurl) {
      signinData.errurl = params?.get('errurl') ?? config?.errurl;
    }

    if (config?.showRememberMe) {
      signinData.rememberMe = values.rememberMe;
    }

    saveSchoolCodeAndDomainFromUNToLocalStorage(transformedUsername, values.schoolCode);

    event(gaEventName, gaEventCategory, 'Submit', `${gaEventCategory} submit`);

    await signIn(signinData, formikBag);
  };

  const { formik, dirtyFields, handleChange, handleSubmit } = useForm<FormValues>(
    { username: '', password: '', schoolCode: '', rememberMe: config?.defaultRememberMe },
    validate,
    handleValidatedSignInValues,
  );

  const handleActiveLearnUsernameChange = useCallback(
    (e: React.ChangeEvent<any>) => {
      if (showSchoolDomain) {
        const username = e.target.value;
        const enteredAtSign = username?.includes('@');
        const shouldShowSchoolDomain = !!schoolDomain && !enteredAtSign;

        setMasterShowSchoolDomain(shouldShowSchoolDomain);
      }

      handleChange(e);
    },
    [schoolDomain],
  );

  const autoLoginSetValues = (username: string, password: string) => {
    formik.setFieldValue('username', username);
    formik.setFieldValue('password', password);
  };

  useEffect(() => {
    let initialErrorMsg: InitialErrorType | undefined;

    const configureInitialError = () => {
      if (config?.socialSigninV2Error) {
        initialErrorMsg = getErrorMessageFromSocialSigninV2Error(config?.socialSigninV2Error);
      }

      if (state?.signupLoginError || state?.autologinError) {
        autoLoginSetValues(state.username, state.password);

        initialErrorMsg = getErrorMessageFromPiErrorCode(state.error?.fault?.detail?.errorcode);
      }

      if (state?.timeout) {
        initialErrorMsg = getErrorMessageFromPiErrorCode(Constants.PIUIERRORCODE_TIMED_OUT_ERROR);
      }

      if (signInErrorId === Constants.PIUIERRORCODE_SMS_SUB_ERROR) {
        initialErrorMsg = getErrorMessageFromPiErrorCode(Constants.PIUIERRORCODE_SMS_SUB_ERROR);
      } else if (signInErrorId) {
        initialErrorMsg = getErrorMessageFromPiErrorCode(Constants.PIUIERRCODE_GENERAL_SERVER_ERROR);
      }

      setGlobalErrorMessage(initialErrorMsg);

      if (isAutoLogin && !initialErrorMsg) {
        const un = params?.get('username') ?? state.username ?? '';
        const pw = params?.get('password') ?? state.password ?? '';
        // TODO Are there other params that need to be auto-populated?
        const autoLoginParams = {
          username: un,
          password: pw,
          rememberMe: false, // TODO Do we need a dynamic value for this?
        };
        autoLoginSetValues(un, pw);
        handleValidatedSignInValues(autoLoginParams, formik);
      }
    };

    const initializeActiveLearn = () => {
      if (showSchoolCode) {
        const localSchoolCode = window.localStorage.getItem('ALSchoolCode') || '';
        formik.setFieldValue('schoolCode', localSchoolCode);
      }

      if (showSchoolDomain) {
        const localSchoolDomain = window.localStorage.getItem('AHSchoolDomain') || '';

        setMasterShowSchoolDomain(!!localSchoolDomain);
        setSchoolDomain(localSchoolDomain);
      }
    };

    configureInitialError();
    initializeActiveLearn();
  }, []);

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setGlobalErrorMessage(Constants.defaultInitialError);
    handleSubmit(e);
  };

  const googleSignIn = getSocialSignIn(config, 'googleV2');
  const microsoftSignIn = getSocialSignIn(config, 'msV2');
  const zainoSignIn = getSocialSignIn(config, 'zd');
  const usernameKidFriendlyIcon = showKidFriendlyIcons ? <SvgIcon name="ACCOUNT" /> : null;
  const passwordKidFriendlyIcon = showKidFriendlyIcons ? <SvgIcon name="LOCK" /> : null;
  const schoolCodeKidFriendlyIcon = showKidFriendlyIcons ? <SvgIcon name="SCHOOL" /> : null;

  return (
    <>
      {globalErrorMessage && <Alert>{showError(globalErrorMessage)}</Alert>}

      <form noValidate onSubmit={submitForm}>
        {showSchoolDomain ? (
          <SchoolDomainInputGroup
            type="text"
            name="username"
            label={isLegacy ? translateText('Username') : translateText('Email address or username')}
            id="username"
            labelClassName="ies-label"
            inputClassName="ies-input"
            errorClassName="ies-input-error"
            helperClassName="ies-input-helper"
            autoComplete="username"
            aria-required
            onBlur={formik.handleBlur}
            onChange={handleActiveLearnUsernameChange}
            value={formik.values.username}
            error={translateText(formik?.errors?.username ?? '')}
            dirty={dirtyFields.username}
            touched={formik.touched.username}
            icon={usernameKidFriendlyIcon}
            additionalInputProps={{ schoolDomain, showSchoolDomain: masterShowSchoolDomain }}
          />
        ) : (
          <InputFormGroup
            type="text"
            name="username"
            label={isLegacy ? translateText('Username') : translateText('Email address or username')}
            id="username"
            labelClassName="ies-label"
            inputClassName="ies-input"
            errorClassName="ies-input-error"
            helperClassName="ies-input-helper"
            autoComplete="username"
            aria-required
            onBlur={formik.handleBlur}
            onChange={handleChange}
            value={formik.values.username}
            error={translateText(formik?.errors?.username ?? '')}
            dirty={dirtyFields.username}
            touched={formik.touched.username}
            icon={usernameKidFriendlyIcon}
          />
        )}

        <PasswordInputGroup
          type="password"
          name="password"
          id="password"
          label={translateText('Password')}
          labelClassName="ies-label"
          inputClassName="ies-input password"
          errorClassName="ies-input-error"
          helperClassName="ies-input-helper"
          autoComplete="current-password"
          aria-required
          onBlur={formik.handleBlur}
          onChange={handleChange}
          value={formik.values.password}
          error={translateText(formik?.errors?.password ?? '')}
          dirty={dirtyFields.password}
          touched={formik.touched.password}
          icon={passwordKidFriendlyIcon}
        />

        {showSchoolCode && (
          <InputFormGroup
            type="text"
            name="schoolCode"
            label={translateText('School code (Primary schools only)')}
            labelClassName="ies-label"
            inputClassName="ies-input password"
            errorClassName="ies-input-error"
            helperClassName="ies-input-helper"
            aria-required
            onBlur={formik.handleBlur}
            onChange={handleChange}
            value={formik.values.schoolCode}
            error={translateText(formik?.errors?.schoolCode)}
            dirty={dirtyFields.schoolCode}
            touched={formik.touched.schoolCode}
            icon={schoolCodeKidFriendlyIcon}
          />
        )}

        <div className="form-group">
          {config?.showRememberMe ? (
            <div className="remember-me-wrapper">
              <SelectionFormGroup
                id="remember-me-tooltip"
                label={translateText('Remember me')}
                name="rememberMe"
                error={translateText(formik?.errors?.rememberMe ?? '')}
                dirty={dirtyFields.rememberMe}
                touched={formik.touched.rememberMe}
                checked={formik.values.rememberMe}
                onChange={handleChange}
              />
              <div>
                <Link to={ROUTES.FORGOT_UN_PW_INTERNAL} onClick={() => eventGoToForgotUnPw()}>
                  {isLegacy ? translateText('Forgot your username or password?') : translateText('Trouble signing in?')}
                </Link>
              </div>
            </div>
          ) : (
            <div className="forgot-unpw-link-wrapper">
              <Link to={ROUTES.FORGOT_UN_PW_INTERNAL} onClick={() => eventGoToForgotUnPw()}>
                {isLegacy ? translateText('Forgot your username or password?') : translateText('Trouble signing in?')}
              </Link>
            </div>
          )}
        </div>

        <Button
          aria-describedby="agreeTermsPrivacy"
          className="ies-btn primary cta-button"
          disabled={formik.isSubmitting}
          id="submitBttn"
          type="submit"
        >
          {formik.isSubmitting
            ? translateText('Signing in...')
            : translateText('Sign in')}
        </Button>
      </form>
      {googleSignIn && <GoogleSignIn from="signin" />}
      {microsoftSignIn && <MicrosoftSignIn />}
      {zainoSignIn && <ZainodigitaleSignIn />}
    </>
  );
}

export default SignInForm;
